import { z } from 'zod';

/**
 * What a question's visibility policy says in **one** context (`03-visibility-policy.md`).
 *
 * Three modes, and the middle one is the whole point: `user_controlled` hands
 * the decision to the member (with a staff-chosen default), while the two
 * `forced_*` modes take it away in opposite directions. An operator who wants
 * "everyone must show their city" and one who wants "nobody may ever expose
 * their civil registry id" are both expressible without code.
 *
 * The mode is stored **per context** — a question carries one for `public` and
 * one for `matched` — which is what makes "hidden while browsing, shown after
 * she accepts" a configuration rather than a feature.
 */
export const VISIBILITY_MODES = ['user_controlled', 'forced_visible', 'forced_hidden'] as const;

export const VisibilityModeSchema = z.enum(VISIBILITY_MODES);

export type VisibilityMode = (typeof VISIBILITY_MODES)[number];

const MODE_SET: ReadonlySet<string> = new Set<string>(VISIBILITY_MODES);

/**
 * Narrows a plain string. Fail-closed by construction: a caller that cannot
 * narrow has no policy, and `resolveVisibility` refuses to guess one — an
 * unknown mode must never fall through to "visible".
 */
export function isVisibilityMode(value: string): value is VisibilityMode {
  return MODE_SET.has(value);
}
